import CardContainer from "@/components/Card/CardContainer";
import TableItemPlaceHolder from "@/components/Placeholder/TableItemPlaceHolder";
import { Svg } from "@/types";
import AttachMoneyRoundedIcon from "@mui/icons-material/AttachMoneyRounded";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import {
  Box,
  Divider,
  IconButton,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Theme,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import MenuItem from "@mui/material/MenuItem";
import * as React from "react";
const MENU_BORDER_RADIUS = 12;

export default function EICustomListItem<ListItem extends string = string>({
  data,
  id,
  itemType = "primary",
  selectList,
  selectItemValue,
  onChangeSelectType,
  onDeleteListItem,
  isLoading,
}: EICustomListItemProps<ListItem>) {
  const [selectValue, setSelectValue] = React.useState<ListItem>(
    selectItemValue ?? selectList?.[0]?.value ?? ""
  );

  React.useEffect(() => {
    if (selectItemValue && selectValue !== selectItemValue)
      setSelectValue(selectItemValue);
  }, [selectItemValue]);
  const handleDeleteListItem = () => {
    onDeleteListItem(id, selectValue);
  };
  return (
    <Box sx={{ display: "flex", width: "100%", gap: 1.5, alignItems: "center" }}>
      <Box sx={{ flexShrink: 0 }}>
        <EICustomButtonMenu<ListItem>
          items={selectList}
          selectValue={selectValue}
          setSelectValue={setSelectValue}
          onChangeSelectType={onChangeSelectType}
          id={id}
        />
      </Box>
      {isLoading ? (
        <Box sx={{ padding: "0 5px", flex: 1 }}>
          <TableItemPlaceHolder />
        </Box>
      ) : (
        <CardContainer
          sx={{
            padding: "0 5px",
            border: "0.2px solid",
            borderColor: "rgba(237, 239,240, 1) !important",
            flex: 1,
            minWidth: 0, // Allow container to shrink
          }}
          shadow={false}
        >
          {itemType === "secondary" ? (
            <ListItemTwo
              data={data}
              handleDeleteListItem={handleDeleteListItem}
            />
          ) : (
            <ListItemOne
              data={data}
              handleDeleteListItem={handleDeleteListItem}
            />
          )}
        </CardContainer>
      )}
    </Box>
  );
}

const MyMenuProps = (theme: Theme) => ({
  sx: {
    "& .MuiPaper-root": {
      borderRadius: `${MENU_BORDER_RADIUS}px`,
      marginTop: theme.spacing(1),
      color:
        theme.palette.mode === "light"
          ? "rgb(55, 65, 81)"
          : theme.palette.grey[300],
      boxShadow:
        "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
      "& .MuiMenu-list": {
        padding: "4px 0",
      },
      "& .MuiMenuItem-root": {
        "& .MuiSvgIcon-root": {
          fontSize: 18,
          color: theme.palette.text.secondary,
          marginRight: theme.spacing(1.5),
        },
        "&:active": {
          backgroundColor: alpha(
            theme.palette.primary.main,
            theme.palette.action.selectedOpacity
          ),
        },
      },
    },
  },
});

function ListItemOne({ data, handleDeleteListItem }: ListItemProps) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        aria-label="left"
        sx={{ 
          display: "flex", 
          flex: 1, 
          alignItems: "center",
          minWidth: 0, // Important for text truncation
        }}
      >
        <Typography
          sx={{
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            overflow: "hidden",
          }}
        >
          {data?.name}
        </Typography>
      </Box>
      <Box
        id="right"
        sx={{
          display: "flex",
          flexShrink: 0, // Prevent button from shrinking
          ml: 1,
        }}
      >
        <IconButton onClick={handleDeleteListItem} size="small">
          <ClearRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function ListItemTwo({ data, handleDeleteListItem }: ListItemProps) {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 1,
        width: "100%",
        alignItems: "center",
      }}
    >
      <Box
        aria-label="left"
        sx={{ 
          display: "flex", 
          flex: 1, 
          gap: 1.5, 
          alignItems: "center",
          minWidth: 0, // Important for proper flex behavior
        }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0, // Allow shrinking for ellipsis
          }}
        >
          <Typography
            sx={{
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              overflow: "hidden",
            }}
          >
            {data?.name || "Webbula > Auto, Cars, and Trucks Owners > Body > 4 Door Sedan"}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, display: "flex", gap: 1, alignItems: "center" }}>
          <Typography variant="captionReg" sx={{ flexShrink: 0 }}>
            {data?.id || "webbula-194499"}
          </Typography>
          <Divider orientation="vertical" flexItem />
          <Typography variant="captionReg" sx={{ flexShrink: 0 }}>
            {data?.altId || "1775605685"}
          </Typography>
        </Box>
      </Box>
      <Box
        id="right"
        sx={{
          display: "flex",
          gap: 1,
          alignItems: "center",
          flexShrink: 0, // Prevent right section from shrinking
          ml: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <AttachMoneyRoundedIcon fontSize="small" />
          <Typography variant="body2">{data?.price || "0.50"}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <GroupsRoundedIcon fontSize="small" />
          <Typography variant="body2">{data?.audience || "14m"}</Typography>
        </Box>
        <Divider orientation="vertical" flexItem />
        <IconButton onClick={handleDeleteListItem} size="small">
          <ClearRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
}

function EICustomButtonMenu<ListItem>({
  items,
  selectValue,
  setSelectValue,
  onChangeSelectType,
  id,
}: EICustomButtonMenuProps<ListItem>) {
  const theme = useTheme();
  const handleChange = (event: SelectChangeEvent<ListItem>) => {
    const { target } = event;

    onChangeSelectType(selectValue, target.value as ListItem, id);
    setSelectValue(target.value as ListItem);
  };
  return (
    <div>
      <Select
        labelId="demo-multiple-name-label"
        id="demo-multiple-name"
        value={selectValue}
        onChange={handleChange}
        input={<OutlinedInput />}
        MenuProps={MyMenuProps(theme)}
        IconComponent={() => null}
        inputProps={{
          sx: {
            padding: "0 !important",
            minHeight: "40px !important",

            borderRadius: `${MENU_BORDER_RADIUS}px`,

            "& .MuiSvgIcon-root": {
              fontSize: 18,
              color: theme.palette.text.secondary,
              marginRight: theme.spacing(1.5),
            },
          },
          disableRipple: true,
        }}
        sx={{
          borderRadius: `${MENU_BORDER_RADIUS}px`,
          border: "0.2px solid",
          borderColor: "rgba(237, 239,240, 1) !important",
          "&:active": {
            borderColor: "rgba(237, 239,240, 1) !important",
            borderWidth: "0.2px",
          },
          minHeight: "33px",
          minWidth: "107px !important",
        }}
        readOnly={items.length < 2}
      >
        {items?.map(({ value, label, icon: Icon }: Item, index: number) => (
          <MenuItem key={index} value={value} disableRipple>
            <Icon />
            {label}
          </MenuItem>
        ))}
      </Select>
    </div>
  );
}
type OnChangeSelect<T> = (a: T, b: T, c: string) => void;
interface EICustomListItemProps<ListItem> {
  data: any;
  id: string;
  itemType?: "primary" | "secondary";
  selectList: Item[];
  selectItemValue: ListItem;
  onChangeSelectType: OnChangeSelect<ListItem>;
  onDeleteListItem: (a: string, b: ListItem) => void;
  isLoading?: boolean;
}
interface ListItemProps {
  data: any;
  handleDeleteListItem: () => void;
}

type Item = {
  label: string;
  value: string;
  icon: Svg;
};
interface EICustomButtonMenuProps<ListItem> {
  items: Item[];
  id: string;
  selectValue: ListItem;
  setSelectValue: React.Dispatch<React.SetStateAction<ListItem>>;
  onChangeSelectType: OnChangeSelect<ListItem>;
}
